import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  User, 
  FileText, 
  Camera, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Upload,
  Shield,
  LogOut
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';
import Header from '../components/Header';

const KYC_STEPS = [
  { id: 1, title: 'Informations personnelles', icon: User },
  { id: 2, title: 'Pièce d\'identité', icon: FileText },
  { id: 3, title: 'Selfie de vérification', icon: Camera },
];

const KYC = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    nationality: 'CI',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
    telegram: '',
    whatsapp: '',
    idType: 'passport',
    idNumber: '',
    idFrontImage: null,
    idBackImage: null,
    selfieImage: null,
  });

  useEffect(() => {
    fetchKycStatus();
  }, []);

  const fetchKycStatus = async () => {
    try {
      const response = await api.get('/kyc/status');
      setKycStatus(response.data);
      if (response.data.status === 'pending' || response.data.status === 'approved') {
        // KYC already submitted
      }
    } catch (error) {
      // No KYC submitted yet
      setKycStatus({ status: 'not_started' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Le fichier ne doit pas dépasser 5 Mo');
        return;
      }
      setFormData(prev => ({ ...prev, [fieldName]: file }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null) {
          submitData.append(key, value);
        }
      });

      await api.post('/kyc/submit', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('KYC soumis avec succès ! Nous vérifions vos documents.');
      setKycStatus({ status: 'pending' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1);
    else handleSubmit();
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const renderStatusBadge = () => {
    if (!kycStatus) return null;
    
    const statusConfig = {
      not_started: { cls: 'bg-gray-100 text-gray-700', icon: AlertCircle, text: 'Non vérifié' },
      pending: { cls: 'bg-amber-100 text-amber-700', icon: Clock, text: 'En cours de vérification' },
      approved: { cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, text: 'Vérifié' },
      rejected: { cls: 'bg-rose-100 text-rose-700', icon: AlertCircle, text: 'Rejeté' },
    };
    
    const config = statusConfig[kycStatus.status] || statusConfig.not_started;
    const Icon = config.icon;
    
    return (
      <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full ${config.cls}`} data-testid="kyc-status-badge">
        <Icon className="w-4 h-4" />
        <span className="font-medium">{config.text}</span>
      </div>
    );
  };

  if (kycStatus?.status === 'approved') {
    return (
      <div className="min-h-screen bg-gray-50">
      <Header />
        
        <div className="pt-10 px-6 pb-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 mb-4">Identité vérifiée</h1>
            <p className="text-gray-500 mb-8">
              Votre compte est entièrement vérifié. Vous avez accès à toutes les fonctionnalités d'alphaswapp.
            </p>
            <Link to="/dashboard">
              <button className="bg-[#191C1F] text-white px-8 py-3 rounded-xl hover:bg-[#191C1F] transition-colors font-semibold">
                Retour au Dashboard
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (kycStatus?.status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50">
      <Header />
        
        <div className="pt-10 px-6 pb-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-yellow-600" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 mb-4">Vérification en cours</h1>
            <p className="text-gray-500 mb-8">
              Nous examinons vos documents. Vous recevrez une notification par email une fois la vérification terminée.
              <br/>Délai estimé : 24-48 heures.
            </p>
            <Link to="/dashboard">
              <button className="bg-[#191C1F] text-white px-8 py-3 rounded-xl hover:bg-[#191C1F] transition-colors font-semibold">
                Retour au Dashboard
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <Header />

      <div className="pt-10 px-6 pb-12">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-[#191C1F]" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Vérification d'identité (KYC)</h1>
            <p className="text-gray-500">Vérifiez votre identité pour accéder à toutes les fonctionnalités</p>
            <div className="mt-4">{renderStatusBadge()}</div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-10">
            {KYC_STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      isCompleted ? 'bg-green-600 text-white' :
                      isActive ? 'bg-[#191C1F] text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {isCompleted ? <CheckCircle className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                    </div>
                    <span className={`text-sm mt-2 font-medium ${isActive ? 'text-[#191C1F]' : 'text-gray-600'}`}>
                      {step.title}
                    </span>
                  </div>
                  {idx < KYC_STEPS.length - 1 && (
                    <div className={`w-20 h-1 mx-2 rounded ${isCompleted ? 'bg-green-600' : 'bg-gray-200'}`}></div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl border border-gray-100 p-8">
            {/* Step 1: Personal Info */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-xl font-extrabold text-gray-900 mb-4">Informations personnelles</h2>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Prénom</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full border border-gray-100 rounded-xl px-4 py-3 focus:border-neon-green/30  outline-none"
                      placeholder="Jean"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Nom</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full border border-gray-100 rounded-xl px-4 py-3 focus:border-neon-green/30  outline-none"
                      placeholder="Dupont"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Date de naissance</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      className="w-full border border-gray-100 rounded-xl px-4 py-3 focus:border-neon-green/30  outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Nationalité</label>
                    <select
                      name="nationality"
                      value={formData.nationality}
                      onChange={handleInputChange}
                      className="w-full border border-gray-100 rounded-xl px-4 py-3 focus:border-neon-green/30  outline-none"
                    >
                      <option value="FR">France</option>
                      <option value="BE">Belgique</option>
                      <option value="CH">Suisse</option>
                      <option value="DE">Allemagne</option>
                      <option value="ES">Espagne</option>
                      <option value="PT">Portugal</option>
                      <option value="GB">Royaume-Uni</option>
                      <option value="US">États-Unis</option>
                      <option value="CA">Canada</option>
                      <option value="OTHER">Autre</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Adresse</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full border border-gray-100 rounded-xl px-4 py-3 focus:border-neon-green/30  outline-none"
                    placeholder="123 Rue Example"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Ville</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full border border-gray-100 rounded-xl px-4 py-3 focus:border-neon-green/30  outline-none"
                      placeholder="Abidjan"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Téléphone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full border border-gray-100 rounded-xl px-4 py-3 focus:border-neon-green/30  outline-none"
                      placeholder="+225 07 08 09 10 11"
                      required
                      data-testid="kyc-phone-input"
                    />
                    {formData.phone && !/^\+?[0-9 ()./-]{8,18}$/.test(formData.phone) && (
                      <p className="text-xs text-rose-600 mt-1" data-testid="phone-invalid-msg">Numéro invalide — format international recommandé (ex : +33 6 12 34 56 78)</p>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
                  <p className="text-xs font-semibold text-blue-800 mb-3">
                    Facultatif mais recommandé — pour que notre service client puisse vous contacter rapidement en cas de besoin :
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Telegram</label>
                      <input
                        type="text"
                        name="telegram"
                        value={formData.telegram}
                        onChange={handleInputChange}
                        className="w-full border border-gray-100 rounded-xl px-4 py-3 focus:border-neon-green/30 outline-none bg-white"
                        placeholder="@votre_username"
                        data-testid="kyc-telegram-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">WhatsApp</label>
                      <input
                        type="tel"
                        name="whatsapp"
                        value={formData.whatsapp}
                        onChange={handleInputChange}
                        className="w-full border border-gray-100 rounded-xl px-4 py-3 focus:border-neon-green/30 outline-none bg-white"
                        placeholder="+33 6 12 34 56 78"
                        data-testid="kyc-whatsapp-input"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-blue-600 mt-3">Vous pouvez aussi nous joindre sur Telegram : <a href="https://t.me/alphaswapp" target="_blank" rel="noreferrer" className="font-bold underline" data-testid="kyc-telegram-link">@alphaswapp</a></p>
                </div>
              </div>
            )}

            {/* Step 2: ID Document */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-extrabold text-gray-900 mb-4">Pièce d'identité</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Type de document</label>
                  <select
                    name="idType"
                    value={formData.idType}
                    onChange={handleInputChange}
                    className="w-full border border-gray-100 rounded-xl px-4 py-3 focus:border-neon-green/30  outline-none"
                  >
                    <option value="passport">Passeport</option>
                    <option value="id_card">Carte d'identité nationale</option>
                    <option value="driver_license">Permis de conduire</option>
                    <option value="residence_permit">Titre de séjour</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Numéro du document</label>
                  <input
                    type="text"
                    name="idNumber"
                    value={formData.idNumber}
                    onChange={handleInputChange}
                    className="w-full border border-gray-100 rounded-xl px-4 py-3 focus:border-neon-green/30  outline-none"
                    placeholder="AB123456"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Recto du document</label>
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-blue-500 transition-colors cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'idFrontImage')}
                        className="hidden"
                        id="idFront"
                      />
                      <label htmlFor="idFront" className="cursor-pointer">
                        {formData.idFrontImage ? (
                          <div className="text-green-600">
                            <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                            <span className="text-sm">{formData.idFrontImage.name}</span>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                            <span className="text-sm text-gray-400">Cliquez pour upload</span>
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Verso du document</label>
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-blue-500 transition-colors cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'idBackImage')}
                        className="hidden"
                        id="idBack"
                      />
                      <label htmlFor="idBack" className="cursor-pointer">
                        {formData.idBackImage ? (
                          <div className="text-green-600">
                            <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                            <span className="text-sm">{formData.idBackImage.name}</span>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                            <span className="text-sm text-gray-400">Cliquez pour upload</span>
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Conseils :</strong> Assurez-vous que votre document est :
                  </p>
                  <ul className="text-sm text-[#191C1F] mt-2 space-y-1">
                    <li>• Valide et non expiré</li>
                    <li>• Entièrement visible et lisible</li>
                    <li>• Sans reflets ni ombres</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Step 3: Selfie */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-xl font-extrabold text-gray-900 mb-4">Selfie de vérification</h2>
                
                <div className="text-center">
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 hover:border-blue-500 transition-colors cursor-pointer max-w-md mx-auto">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'selfieImage')}
                      className="hidden"
                      id="selfie"
                    />
                    <label htmlFor="selfie" className="cursor-pointer">
                      {formData.selfieImage ? (
                        <div className="text-green-600">
                          <CheckCircle className="w-12 h-12 mx-auto mb-2" />
                          <span>{formData.selfieImage.name}</span>
                        </div>
                      ) : (
                        <>
                          <Camera className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                          <p className="text-gray-500 mb-2">Prenez un selfie avec votre document</p>
                          <span className="text-sm text-gray-400">Cliquez pour upload</span>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-xl p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Instructions :</strong>
                  </p>
                  <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                    <li>• Tenez votre pièce d'identité à côté de votre visage</li>
                    <li>• Votre visage et le document doivent être clairement visibles</li>
                    <li>• Bonne luminosité, pas de lunettes de soleil ni chapeau</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                className="px-6 py-3 rounded-xl border border-gray-100 text-gray-600 font-medium hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Précédent
              </button>
              <button
                onClick={nextStep}
                disabled={loading}
                className="px-8 py-3 rounded-xl bg-[#191C1F] text-white font-medium hover:bg-[#191C1F] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Envoi en cours...' : currentStep === 3 ? 'Soumettre' : 'Suivant'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KYC;
